import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface BusinessModel {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface BusinessModelSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const BusinessModelSelect: React.FC<BusinessModelSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  
  const businessModels: BusinessModel[] = [
    {
      id: 'B2B',
      name: t('brands.edit.marketingStrategy.businessModel.b2b', 'B2B'),
      emoji: '🏢',
      description: t('brands.edit.marketingStrategy.businessModel.b2bDescription', 'Business-to-business (selling to other companies)')
    },
    {
      id: 'B2C',
      name: t('brands.edit.marketingStrategy.businessModel.b2c', 'B2C'),
      emoji: '👥',
      description: t('brands.edit.marketingStrategy.businessModel.b2cDescription', 'Business-to-consumer (selling directly to end consumers)')
    },
    {
      id: 'B2B2C',
      name: t('brands.edit.marketingStrategy.businessModel.b2b2c', 'B2B2C'),
      emoji: '🔗',
      description: t('brands.edit.marketingStrategy.businessModel.b2b2cDescription', 'Business-to-business-to-consumer (selling through partners to consumers)')
    },
    {
      id: 'B2G',
      name: t('brands.edit.marketingStrategy.businessModel.b2g', 'B2G'),
      emoji: '🏛️',
      description: t('brands.edit.marketingStrategy.businessModel.b2gDescription', 'Business-to-government (selling to government entities)')
    },
    {
      id: 'Other',
      name: t('brands.edit.marketingStrategy.businessModel.other', 'Other'),
      emoji: '🔄',
      description: t('brands.edit.marketingStrategy.businessModel.otherDescription', 'Other business model not listed above')
    }
  ];
  
  const selectedModel = businessModels.find(model => model.id === value) || businessModels[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedModel.emoji}</span>
          <span className="text-gray-700">{selectedModel.name}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {businessModels.map(model => (
                <button
                  type="button"
                  key={model.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(model.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    model.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{model.emoji}</span>
                    <div>
                      <div className="font-medium">{model.name}</div>
                      <div className="text-xs text-gray-500">{model.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 